import React, { useEffect, useRef, useState } from "react";

export type Chip = {
    id: string;
    label: string;
    active: boolean;
    onToggle: () => void;
};

export type Category = {
    id: number | string;
    name: string;
};

type Props = {
    title: string;
    /** Поиск */
    searchPlaceholder?: string;
    query?: string;                 // controlled (если хочешь контролировать снаружи)
    defaultQuery?: string;          // uncontrolled (если не передаёшь query)
    debounceMs?: number;            // по умолчанию 300
    onSearch?: (value: string) => void;

    /** Чипы-переключатели */
    chips?: Chip[];

    /** Категории (горизонтальная лента) */
    categories?: Category[];
    activeCategoryId?: Category["id"] | null;
    onSelectCategory?: (id: Category["id"] | null) => void;

    /** Сброс всех фильтров */
    onReset?: () => void;

    /** Справа (кнопки действий, слоты) */
    rightActions?: React.ReactNode;

    className?: string;
};

export default function ListHeader({
    title,
    searchPlaceholder = "Search…",
    query,
    defaultQuery = "",
    debounceMs = 300,
    onSearch,
    chips = [],
    categories = [],
    activeCategoryId = null,
    onSelectCategory,
    onReset,
    rightActions,
    className = "",
}: Props) {
    // поддерживаем и controlled, и uncontrolled режимы
    const isControlled = typeof query === "string";
    const [innerQuery, setInnerQuery] = useState(defaultQuery);
    const value = isControlled ? (query as string) : innerQuery;

    const debounceRef = useRef<number | null>(null);

    const handleChange = (v: string) => {
        if (isControlled) {
            // controlled: сразу дергаем дебаунс-коллбек
            if (!onSearch) return;
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
            debounceRef.current = window.setTimeout(() => onSearch(v), debounceMs);
        } else {
            // uncontrolled: меняем локальный стейт и дергаем коллбек с дебаунсом
            setInnerQuery(v);
            if (onSearch) {
                if (debounceRef.current) window.clearTimeout(debounceRef.current);
                debounceRef.current = window.setTimeout(() => onSearch(v), debounceMs);
            }
        }
    };

    useEffect(() => {
        return () => {
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
        };
    }, []);

    const hasChips = chips && chips.length > 0;
    const hasCategories = categories && categories.length > 0;

    return (
        <div className={`flex flex-col gap-3 md:flex-row md:items-start md:justify-between ${className}`}>
            {/* Left */}
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-semibold">{title}</h1>
                </div>

                {/* Search */}
                <div className="mt-2 relative max-w-xl">
                    <input
                        value={value}
                        onChange={(e) => handleChange(e.target.value)}
                        placeholder={searchPlaceholder}
                        aria-label="Search"
                        className="w-full rounded-md border border-gray-300 px-10 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <svg
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    >
                        <circle cx="11" cy="11" r="7" strokeWidth="2"></circle>
                        <path d="M20 20L17 17" strokeWidth="2"></path>
                    </svg>

                    {value ? (
                        <button
                            onClick={() => {
                                handleChange("");
                                if (!isControlled) setInnerQuery("");
                            }}
                            aria-label="Clear search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    ) : null}
                </div>

                {/* Chips */}
                {hasChips && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {chips.map((chip) => (
                            <button
                                key={chip.id}
                                onClick={chip.onToggle}
                                className={`px-3 py-1 rounded-full text-xs border
                  ${chip.active
                                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                aria-pressed={chip.active}
                            >
                                {chip.label}
                            </button>
                        ))}

                        {onReset && (
                            <button
                                onClick={onReset}
                                className="px-3 py-1 rounded-full text-xs border border-gray-300 text-gray-700 hover:bg-gray-50"
                                title="Reset filters"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                )}

                {/* Categories */}
                {hasCategories && (
                    <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
                        <button
                            className={`shrink-0 px-3 py-1 border rounded-md text-sm
                ${activeCategoryId === null
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                                }`}
                            onClick={() => onSelectCategory?.(null)}
                        >
                            All
                        </button>

                        {categories.map((c) => (
                            <button
                                key={c.id}
                                className={`shrink-0 px-3 py-1 border rounded-md text-sm
                  ${activeCategoryId === c.id
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                                    }`}
                                onClick={() => onSelectCategory?.(c.id)}
                                title={c.name}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Right */}
            <div className="mt-2 md:mt-0 flex items-start gap-2">
                {rightActions}
            </div>
        </div>
    );
}